/**
 * @license
 * @copyright Copyright Motili Inc., 2023 All Rights Reserved
 */

import React from 'react';
import { styled } from 'styled-components';

import {
    ButtonPrimary,
    ButtonSecondary,
    ButtonProps,
    ButtonDestructive,
} from '@motili/ui';
import Config from '../../../Config';

interface MotiliButtonProps extends Partial<ButtonProps> {
    onClick: (e: any) => any;
    text?: string | JSX.Element;
    disabled?: boolean;
    block?: boolean;
    type?: string;
    id?: string;
    active?: boolean;
    size?: 'sm' | 'md' | 'lg';
    moStyle?: 'normal' | 'primary' | 'red';
    dialog?: boolean;
    fullPage?: boolean;
    busy?: boolean;
    style?: React.CSSProperties;
    color?: string;
    className?: string;
    dataTestId?: string;
}

const MotiliButton = ({
    size = 'md',
    moStyle = 'primary',
    busy = false,
    color,
    dataTestId = 'motili-ui-button',
    ...props
}: MotiliButtonProps) => {
    return (
        <>
            {moStyle === 'normal' && (
                <StyledSecondaryButton
                    disabled={props.disabled || busy}
                    onClick={props.onClick}
                    buttonStyle={{
                        borderColor: '#CCC',
                        ...props.style,
                    }}
                    color={color}
                    dataTestId={dataTestId}
                    spinnerSize='small'
                    spinnerColor={styles.busySpinner[moStyle].color}
                    busy={busy}
                    size={size}
                >
                    {props.text}
                </StyledSecondaryButton>
            )}
            {moStyle === 'primary' && (
                <StyledPrimaryButton
                    disabled={props.disabled || busy}
                    onClick={props.onClick}
                    buttonStyle={{
                        ...props.style,
                    }}
                    color={color}
                    dataTestId={dataTestId}
                    spinnerSize='small'
                    spinnerColor={styles.busySpinner[moStyle].color}
                    busy={busy}
                    size={size}
                >
                    {props.text}
                </StyledPrimaryButton>
            )}
            {moStyle === 'red' && (
                <StyledButtonDestructive
                    disabled={props.disabled}
                    onClick={props.onClick}
                    buttonStyle={{
                        ...props.style,
                    }}
                    dataTestId={dataTestId}
                    spinnerSize='small'
                    spinnerColor={styles.busySpinner[moStyle].color}
                    busy={busy}
                    size={size}
                >
                    {props.text}
                </StyledButtonDestructive>
            )}
        </>
    );
};

const styleBySize = (size?: 'sm' | 'md' | 'lg') => {
    switch (size) {
        case 'sm':
            return `
                font-size: 12px;
                height: 32px;
                padding: 0 16px;
            `;
        case 'lg':
            return `
                font-size: 16px;
                height: 44px;
                padding: 0 24px;
            `;
        default:
            return `
                font-size: 14px;
                height: 40px;
                padding: 0 20px;
            `;
    }
};

const StyledPrimaryButton = styled(ButtonPrimary)<MotiliButtonProps>`
    background-color: ${props => props.color || Config.color.motiliLightBlue};
    ${props => styleBySize(props.size)}
`;

const StyledSecondaryButton = styled(ButtonSecondary)<MotiliButtonProps>`
    background-color: ${props => props.color || 'white'};
    ${props => styleBySize(props.size)}
`;

const StyledButtonDestructive = styled(ButtonDestructive)<MotiliButtonProps>`
    ${props => styleBySize(props.size)}
`;

const styles = {
    busySpinner: {
        normal: {
            color: Config.color.motiliLightBlue,
        },
        primary: {
            color: Config.color.white,
        },
        red: {
            color: Config.color.white,
        },
    },
};

export default MotiliButton;
